import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import {Link as RouterLink} from 'react-router';
import {getUsersByIds} from '../../../firebase/firestore/users';
import {fetchRelatedFromSelection} from '../collectionTaskApi';
import PersonCell, {indexUsersById} from './PersonCell';
import {
    formatRelation,
    formatSimilarityScorePct,
    resolveEeiNavPath,
    resolveIcrNavPath,
} from '../../collection-requests/similarity/similarityLabels';

function entityNumber(entity) {
    return entity?.number || entity?.eeiNumber || entity?.icrNumber || '';
}

function entityTitle(entity) {
    return (entity?.title || entity?.text || '').trim();
}

function entityHref(entity) {
    if (!entity?.type) return null;
    if (entity.type === 'ICR') return resolveIcrNavPath(entity);
    if (entity.type === 'EEI') return resolveEeiNavPath(entity);
    return null;
}

function relationPhrase(edge) {
    const relation = edge?.relation;
    if (relation === 'EQUIVALENT_TO') return 'Equivalent to';
    if (relation === 'INCLUDED_INTO') return 'Included into';
    if (relation === 'OVERLAPS') {
        const pct = formatSimilarityScorePct(edge?.score);
        return pct ? `Overlaps with (${pct})` : 'Overlaps with';
    }
    return formatRelation(relation);
}

/**
 * Pick the suggested (addable) entity vs the original selected anchor.
 */
function splitCandidateAndOriginal(edge) {
    const subject = edge?.subject;
    const target = edge?.target;
    const subjectSelected = Boolean(edge?.subjectSelected);
    const targetSelected = Boolean(edge?.targetSelected);

    if (!subjectSelected && targetSelected) {
        return {candidate: subject, original: target};
    }
    if (subjectSelected && !targetSelected) {
        return {candidate: target, original: subject};
    }
    // Both or neither selected — prefer subject as candidate for included-into orientation.
    return {candidate: subject, original: target};
}

function toLinkedEei(entity) {
    return {
        id: entity.id,
        eeiNumber: entityNumber(entity),
        text: entityTitle(entity) || entity.text || '',
        icrId: entity.icrId || null,
    };
}

function RelatedRequirementRow({edge, alreadyLinked, onAdd, people}) {
    const {candidate, original} = splitCandidateAndOriginal(edge);
    if (!candidate?.id) return null;

    const href = entityHref(candidate);
    const number = entityNumber(candidate) || candidate.id;
    const title = entityTitle(candidate);
    const originalNumber = entityNumber(original) || original?.id || '';
    const originalTitle = entityTitle(original);
    const createdAt = candidate.createdAt
        ? new Date(candidate.createdAt).toLocaleDateString()
        : null;
    const authorId = candidate.authorUserId || null;

    return (
        <Stack
            direction={{xs: 'column', sm: 'row'}}
            spacing={1}
            alignItems={{xs: 'stretch', sm: 'flex-start'}}
            sx={{
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': {borderBottom: 0},
            }}
        >
            <Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {candidate.priority && (
                        <Chip size="small" label={candidate.priority} variant="outlined"/>
                    )}
                    {href ? (
                        <Link
                            component={RouterLink}
                            to={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            variant="body2"
                            fontFamily="monospace"
                            fontWeight={600}
                        >
                            {number}
                        </Link>
                    ) : (
                        <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                            {number}
                        </Typography>
                    )}
                    {title && (
                        <Typography variant="body2" sx={{wordBreak: 'break-word'}}>
                            {title}
                        </Typography>
                    )}
                    {authorId && (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography variant="caption" color="text.secondary">by</Typography>
                            <Box onClick={(e) => e.stopPropagation()} sx={{'& .MuiTypography-root': {fontSize: '0.8rem'}}}>
                                <PersonCell userId={authorId} profile={people[authorId]}/>
                            </Box>
                        </Stack>
                    )}
                    {createdAt && (
                        <Typography variant="caption" color="text.secondary">
                            {createdAt}
                        </Typography>
                    )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{wordBreak: 'break-word'}}>
                    {relationPhrase(edge)}{' '}
                    <Box component="span" sx={{fontFamily: 'monospace', fontWeight: 600}}>
                        {originalNumber}
                    </Box>
                    {originalTitle ? ` ${originalTitle}` : ''}
                </Typography>
            </Stack>
            <Button
                size="small"
                variant="outlined"
                disabled={alreadyLinked || !onAdd}
                onClick={() => onAdd?.(candidate)}
                sx={{flexShrink: 0, alignSelf: {xs: 'flex-end', sm: 'center'}}}
            >
                {alreadyLinked ? 'Added' : 'Add'}
            </Button>
        </Stack>
    );
}

/**
 * Shows similarity-related EEIs for the current Collection Task selection,
 * with an Add action to link them.
 */
export default function RelatedRequirementsFromSelection({
    eeiIds = [],
    linkedEeis = [],
    onAdd,
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [payload, setPayload] = useState(null);
    const [people, setPeople] = useState({});

    const selectionKey = useMemo(
        () => [...eeiIds].sort().join(','),
        [eeiIds],
    );

    const linkedEeiIds = useMemo(
        () => new Set((linkedEeis || []).map((e) => e.id).filter(Boolean)),
        [linkedEeis],
    );

    useEffect(() => {
        if (!eeiIds.length) {
            setPayload(null);
            setError(null);
            setLoading(false);
            setPeople({});
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchRelatedFromSelection({eeiIds});
                if (cancelled) return;
                setPayload(data);
                const edges = Array.isArray(data?.edges) ? data.edges : [];
                const authorIds = [...new Set(edges.flatMap((edge) => {
                    const {candidate} = splitCandidateAndOriginal(edge);
                    return candidate?.authorUserId ? [candidate.authorUserId] : [];
                }))];
                if (authorIds.length) {
                    setPeople(indexUsersById(await getUsersByIds(authorIds)));
                } else {
                    setPeople({});
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e?.message || 'Failed to load related requirements');
                    setPayload(null);
                    setPeople({});
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [selectionKey, eeiIds]);

    if (!eeiIds.length) {
        return null;
    }

    const edges = (Array.isArray(payload?.edges) ? payload.edges : [])
        .filter((edge) => splitCandidateAndOriginal(edge).candidate?.type === 'EEI');
    const overlapMin = payload?.overlapMinPercent ?? 75;

    const handleAdd = (candidate) => {
        if (!candidate?.id || candidate.type !== 'EEI' || !onAdd) return;
        if (linkedEeiIds.has(candidate.id)) return;
        onAdd({type: 'EEI', item: toLinkedEei(candidate)});
    };

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                bgcolor: 'background.paper',
            }}
        >
            <Stack spacing={1}>
                <Typography variant="subtitle2">
                    Related requirements
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Equivalent · Overlaps ≥ {overlapMin}% · Included into selected
                </Typography>

                {loading && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16}/>
                        <Typography variant="body2" color="text.secondary">Loading relations…</Typography>
                    </Stack>
                )}
                {error && <Alert severity="warning">{error}</Alert>}

                {!loading && !error && edges.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No equivalent, high-overlap, or included-into relations for this selection.
                    </Typography>
                )}

                {!loading && edges.length > 0 && (
                    <Stack>
                        {edges.map((edge, idx) => {
                            const {candidate} = splitCandidateAndOriginal(edge);
                            const alreadyLinked = linkedEeiIds.has(candidate?.id);
                            return (
                                <RelatedRequirementRow
                                    key={`${edge?.fromType}:${edge?.fromId}:${edge?.toType}:${edge?.toId}:${edge?.relation}:${idx}`}
                                    edge={edge}
                                    alreadyLinked={alreadyLinked}
                                    onAdd={handleAdd}
                                    people={people}
                                />
                            );
                        })}
                    </Stack>
                )}
            </Stack>
        </Box>
    );
}
